import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, getPostAuthDestination } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

const COUNTRY_CODES = [
  { code: "+962", label: "🇯🇴 +962" },
];

const COOLDOWN_SECONDS = 180;
const OTP_LENGTH = 4;

function getSendErrorMessage(detail, t) {
  const msg = (detail || "").toLowerCase();
  if (msg.includes("cooldown") || msg.includes("wait")) return t("phone.cooldown_error");
  if (msg.includes("hourly") || msg.includes("hour")) return t("phone.hourly_limit");
  if (msg.includes("daily") || msg.includes("day")) return t("phone.daily_limit");
  if (msg.includes("global") || msg.includes("unavailable")) return t("phone.global_limit");
  if (msg.includes("already") || msg.includes("existing")) return t("phone.existing_code");
  return detail;
}

function getVerifyErrorMessage(detail, t) {
  const msg = (detail || "").toLowerCase();
  if (msg.includes("expired")) return t("phone.code_expired");
  if (msg.includes("invalid") || msg.includes("wrong") || msg.includes("incorrect")) return t("phone.invalid_code");
  if (msg.includes("too many") || msg.includes("attempts")) return t("phone.too_many_attempts");
  return detail || t("phone.invalid_code");
}

function isNetworkError(err) {
  return !err?.response || err?.code === "ERR_NETWORK";
}

export default function VerifyPhone({ variant = "page", onVerified = () => {}, onClose = () => {} }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState("input");
  const [countryCode, setCountryCode] = useState("+962");
  const [phone, setPhone] = useState("");
  const [codeDigits, setCodeDigits] = useState(["", "", "", ""]);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState("");
  const [sendingCode, setSendingCode] = useState(false);

  const verifyingRef = useRef(false);
  const inputRefs = useRef([]);
  const initialFocusDone = useRef(false);

  const fullPhone = countryCode + phone;

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (step === "otp" && !initialFocusDone.current) {
      initialFocusDone.current = true;
      setTimeout(() => inputRefs.current[0]?.focus(), 150);
    }
  }, [step]);

  const handleSendCode = async () => {
    if (!phone.trim() || sendingCode) return;
    setError("");
    setSendingCode(true);
    try {
      await api.post("/auth/phone/send-otp", { phone: fullPhone });
      setStep("otp");
      setCountdown(COOLDOWN_SECONDS);
      initialFocusDone.current = false;
    } catch (err) {
      if (isNetworkError(err)) {
        toast.error(formatApiError(err?.response?.data?.detail));
      } else {
        const detail = formatApiError(err?.response?.data?.detail);
        setError(getSendErrorMessage(detail, t));
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || sendingCode) return;
    setError("");
    setCodeDigits(["", "", "", ""]);
    setSendingCode(true);
    try {
      await api.post("/auth/phone/send-otp", { phone: fullPhone });
      setCountdown(COOLDOWN_SECONDS);
      toast.success(t("phone.code_resent"));
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      if (isNetworkError(err)) {
        toast.error(formatApiError(err?.response?.data?.detail));
      } else {
        const detail = formatApiError(err?.response?.data?.detail);
        setError(getSendErrorMessage(detail, t));
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async (code) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setError("");
    try {
      await api.post("/auth/phone/verify-otp", { phone: fullPhone, code });
      setStep("success");
      setTimeout(() => {
        onVerified();
        if (variant === "page") {
          const redirect = searchParams.get("redirect");
          if (redirect) {
            navigate(redirect, { replace: true });
          } else {
            navigate(getPostAuthDestination(user, ""), { replace: true });
          }
        }
      }, 1500);
    } catch (err) {
      if (isNetworkError(err)) {
        toast.error(formatApiError(err?.response?.data?.detail));
      } else {
        const detail = formatApiError(err?.response?.data?.detail);
        setError(getVerifyErrorMessage(detail, t));
      }
      setCodeDigits(["", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      verifyingRef.current = false;
    }
  };

  const handleDigitChange = (index, value) => {
    if (value.length > 1) return;
    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);
    setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const code = newDigits.join("");
    if (code.length === OTP_LENGTH) {
      handleVerify(code);
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePhoneKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendCode();
    }
  };

  const primaryButtonClass = variant === "page"
    ? "btn-volt w-full py-3 text-xs disabled:cursor-not-allowed disabled:opacity-60"
    : "btn-volt w-full py-2.5 text-xs disabled:cursor-not-allowed disabled:opacity-60";

  const isPage = variant === "page";

  const cardClassName = `bg-card border border-border rounded-sm ${isPage ? "p-6 sm:p-8" : "p-5 sm:p-6"}`;

  const cardContent = (
    <div className={cardClassName}>
      {/* Close button for modal */}
      {!isPage && (
        <div className="flex justify-end mb-2">
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t("common.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Logo / Title */}
      <div className="text-center mb-6">
        <h1 className={`font-heading ${isPage ? "text-3xl" : "text-2xl"} tracking-tight text-foreground`}>
          {t("phone.verify_title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("phone.verify_subtitle")}
        </p>
      </div>

      {/* Phone Input Step */}
      {step === "input" && (
        <div>
          <label className="label-eyebrow block mb-2">{t("phone.enter_phone")}</label>
          <div className="flex gap-2">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-3 text-sm text-foreground focus:border-volt focus:outline-none appearance-none cursor-pointer"
            >
              {COUNTRY_CODES.map((cc) => (
                <option key={cc.code} value={cc.code}>
                  {cc.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              inputMode="numeric"
              placeholder="7XXXXXXXX"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.replace(/\D/g, ""));
                setError("");
              }}
              onKeyDown={handlePhoneKeyDown}
              maxLength={15}
              className="flex-1 bg-background border border-border rounded-sm px-4 py-3 text-sm text-foreground focus:border-volt focus:outline-none text-left"
              dir="ltr"
            />
          </div>

          {error && (
            <div className="mt-3 rounded-sm border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-200">
              {error}
            </div>
          )}

          <button
            onClick={handleSendCode}
            disabled={!phone.trim() || sendingCode}
            className={`mt-4 ${primaryButtonClass}`}
          >
            {sendingCode ? t("common.loading") : t("phone.send_code")}
          </button>
        </div>
      )}

      {/* OTP Input Step */}
      {step === "otp" && (
        <div>
          <p className="text-sm text-muted-foreground mb-4 text-center">
            {t("phone.enter_code", { phone: fullPhone })}
          </p>

          <div className="flex justify-center gap-3 mb-4" dir="ltr">
            {codeDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => handleDigitKeyDown(index, e)}
                className="w-12 h-14 text-center text-xl bg-background border border-border rounded-sm text-foreground focus:border-volt focus:outline-none"
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {error && (
            <div className="mb-3 rounded-sm border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-200">
              {error}
            </div>
          )}

          <div className="text-center">
            {countdown > 0 ? (
              <span className="text-sm text-muted-foreground">
                {t("phone.resend_in", { seconds: countdown })}
              </span>
            ) : (
              <button
                onClick={handleResend}
                disabled={sendingCode}
                className="text-sm text-volt hover:underline underline-offset-4 disabled:opacity-50"
              >
                {sendingCode ? t("common.loading") : t("phone.resend_code")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Success State */}
      {step === "success" && (
        <div className="text-center py-6">
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-volt/10 flex items-center justify-center">
            <Check className="h-8 w-8 text-volt animate-in zoom-in" />
          </div>
          <h2 className="font-heading text-xl tracking-tight text-foreground">{t("phone.verified")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("phone.verified_message")}</p>
        </div>
      )}
    </div>
  );

  if (isPage) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-start justify-center pt-20 px-4">
        <div className="w-full max-w-md">
          {cardContent}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        {cardContent}
      </div>
    </div>
  );
}
